import { FCC } from "@/types/common";
import { TGetCompanyDto } from "@/api/services/main/company/dtos/get-company.dto";
import { useCompanyUserList } from "@/api/services/main/company-user";
import { CompanyTitle } from "@/features/company/components/CompanyTitle";
import { Grid, Typography } from "@mui/material";
import { CompanyBasicInfo } from "@/features/company/components/company-detail/CompanyBasicInfo";
import { CompanyContactInfo } from "@/features/company/components/company-detail/CompanyContactInfo";
import { CompanyContractInfo } from "@/features/company/components/company-detail/CompanyContractInfo";
import { useNavigate, useParams } from "react-router-dom";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { CompanyAdditionalDetailInfo } from "@/features/company/components/company-detail/CompanyAdditionalDetailInfo";
import { CompanyUserSection } from "@/features/company/components/company-detail/CompanyUserSection";
import { CompanyVisaLinkSection } from "@/features/company/components/company-detail/CompanyVisaLinkSection";
import { CompanyHandledIndustryDetailSection } from "@/features/company/components/company-detail/CompanyHandledIndustryDetailSection";
import { CompanyFooterContainer } from "@/features/company/components/CompanyFooterContainer";
import { Button } from "@/components/elements/Button";
import { AppRoutes } from "@/routes/config";
import { useEffect, useMemo } from "react";
import { getDynamicRoute } from "@/utils/url";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { USER_TYPE } from "@/types/enum";
import { IconEditor } from "@/assets/icons";
import { truncateWithEllipsis } from "@/utils/string";

type TCompanyDetailProps = {
  defaultValues?: TGetCompanyDto;
};
export const CompanyDetailContent: FCC<TCompanyDetailProps> = ({ defaultValues }) => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { userType, user } = useInitSetup();
  const isRSOUser = userType === USER_TYPE.RSO_USER;
  const isCompanyUser = userType === USER_TYPE.COMPANY_USER;
  const { isLoading: isCompanyUserLoading, data: usersResponse } = useCompanyUserList(isRSOUser ? companyId : undefined)
  const users = usersResponse?.data || [];
  const learners = defaultValues?.learners ?? [];
  const hasLearners = learners.length > 0;
  const activeLearnerCount = learners.filter((l) => !l.isArchive).length;
  const canArchiveCompany = isRSOUser && hasLearners;
  const canDeleteCompany = isRSOUser && !hasLearners;
  const hasEndDate = !!defaultValues?.endDate;

  useEffect(() => {
    if (!companyId) return navigate(AppRoutes.companyList);
  }, [companyId]);

  const editRoute = getDynamicRoute(AppRoutes.companyEdit, { companyId: companyId });

  // いろはな限定機能: irohana visa 連携は独立企業 (rso_id NULL) のみ対象。
  // RSO配下の企業 (rsoId あり) は親RSOの連携を通じて接続するため、企業詳細では非表示にする。
  const isIndependentCompany = defaultValues?.rsoId == null;
  // いろはな限定機能のため、irohana担当者 (isUser === false) のみ閲覧可能。顧客 (isUser true) には非表示。
  const isIrohanaStaff = user?.isUser === false;

  const tabs = useMemo(() => {
    if (!defaultValues) return [];
    const base = [
      ...(isIndependentCompany && isIrohanaStaff
        ? [{ title: "irohana visa 連携", content: <CompanyVisaLinkSection data={defaultValues} /> }]
        : []),
      { title: "基本情報", content: <CompanyBasicInfo defaultValues={defaultValues} /> },
      ...(isRSOUser ? [{ title: "担当者情報", content: <CompanyContactInfo defaultValues={defaultValues} /> }] : []),
      { title: "その他の情報", content: <CompanyAdditionalDetailInfo defaultValues={defaultValues} /> },
      ...(isCompanyUser
        ? [{ title: "取り扱う分野", content: <CompanyHandledIndustryDetailSection companyData={defaultValues} /> }]
        : []),
      ...(isCompanyUser
        ? [{
          title: "契約情報",
          content: (
            <CompanyContractInfo data={defaultValues} />
          ),
        }]
        : []),
      ...(isRSOUser ? [{
        title: "発行済みユーザー",
        content: <CompanyUserSection
          companyId={companyId}
          isLoading={isCompanyUserLoading}
          users={users}
        />
      }] : []),
    ];
    return base;
  }, [defaultValues, isIndependentCompany, isIrohanaStaff, isRSOUser, isCompanyUser, isCompanyUserLoading, users]);

  if (!defaultValues) return null;

  return (
    <>
      <CompanyTitle
        title={defaultValues.companyName}
        managementNumber={defaultValues.managementNumber}
        createdAt={defaultValues.createdAt}
        updatedAt={defaultValues.updatedAt}
      />
      <Grid
        container
        sx={{
          marginTop: 4,
        }}
      >
        <Grid item xs={12}>
          <MenuNavigate
            menuPrefix={
              <Typography variant="body14" sx={{ color: "#0B0C0D", px: 2, py: 1, fontWeight: 700 }}>
                {truncateWithEllipsis(defaultValues.companyName, 15)}
              </Typography>
            }
            contents={tabs}
            contentAffix={
              (isRSOUser || isCompanyUser) && (
                <CompanyFooterContainer
                  isShowSetArchiveButton={canArchiveCompany}
                  isArchive={defaultValues.isArchive}
                  isShowDeleteButton={canDeleteCompany}
                  hasEndDate={hasEndDate}
                  activeLearnerCount={activeLearnerCount}
                  isDetailPage={true}
                >
                  <Button variant="outline" size="sm" onClick={() => navigate(editRoute)} startIcon={<IconEditor />}>
                    編集
                  </Button>
                </CompanyFooterContainer>
              )
            }
            contentWidth="900px"
          />
        </Grid>
      </Grid>
    </>
  );
};
